import { fabric } from 'fabric';
import { useCallback, useEffect, useRef } from 'react';

import { getCanvasStateManager } from './canvasStateManager';

import {
  APPLIANCE_SELECTION_BORDER_THICKNESS,
  DEFAULT_ZOOM,
} from '~/core/constants';
import { isCanvasSafe } from '~/core/utils/canvasSafety';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectFabricCanvas, updateCanvas } from '~/redux/slices/fabric';
import theme from '~/theme/theme';

const { secondaryColor } = theme.light.defaultTheme;

const useFabricCanvas = () => {
  const dispatch = useAppDispatch();

  const canvas = useAppSelector(selectFabricCanvas);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDisposingRef = useRef(false);
  const isInitializingRef = useRef(false);
  const canvasDisposalTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const stateManager = getCanvasStateManager();

  const initCanvas = useCallback(() => {
    if (!canvasRef.current || isInitializingRef.current) {
      return;
    }

    if (canvasDisposalTimeoutRef.current) {
      clearTimeout(canvasDisposalTimeoutRef.current);
      canvasDisposalTimeoutRef.current = null;
    }

    // Reset disposing flag
    isDisposingRef.current = false;
    isInitializingRef.current = true;

    try {
      const canvasOptions: fabric.ICanvasOptions = {
        preserveObjectStacking: false,
        selection: true,
        defaultCursor: 'default',
        fireRightClick: false,
        stopContextMenu: true,
      };

      const newCanvas = new fabric.Canvas(canvasRef.current, canvasOptions);

      if (isCanvasSafe(newCanvas)) {
        newCanvas.renderAll();
        newCanvas.setZoom(DEFAULT_ZOOM);
        dispatch(updateCanvas(newCanvas));

        fabric.Object.prototype.set({
          borderColor: secondaryColor,
          borderScaleFactor: APPLIANCE_SELECTION_BORDER_THICKNESS,
          objectCaching: true,
        });
      } else {
        try {
          stateManager.markCanvasDisposed(newCanvas);
          newCanvas.dispose();
        } catch (error) {
          console.warn('Error disposing failed canvas:', error);
        }
      }
    } catch (error) {
      console.error('Error initializing canvas:', error);
    } finally {
      isInitializingRef.current = false;
    }
  }, [dispatch, canvasRef, stateManager]);

  useEffect(() => {
    if (canvasRef?.current) {
      initCanvas();
    }

    return () => {
      canvasRef.current = null;
    };
  }, [canvasRef, dispatch, initCanvas]);

  // stop elements on drawing area from being draggable
  useEffect(() => {
    if (canvas && isCanvasSafe(canvas) && !isDisposingRef.current) {
      try {
        canvas.selection = false;
      } catch (error) {
        console.warn('Error setting canvas selection:', error);
      }
    }
  }, [canvas]);

  useEffect(
    () => () => {
      if (canvasDisposalTimeoutRef.current) {
        clearTimeout(canvasDisposalTimeoutRef.current);
      }
    },
    []
  );

  return {
    canvasRef,
    isCanvasSafe,
    isDisposing: isDisposingRef.current,
    isInitializing: isInitializingRef.current,
    stateManager,
  };
};

export default useFabricCanvas;
